// Modal Dirty-State
//
// Vergleicht den ephemeren Livewire-State (`$wire`) der getrackten Properties mit
// der Baseline, die beim Öffnen bzw. nach dem Speichern gezogen wird. Der Server
// signalisiert eine neue Baseline über `$wire.dirtyRevision` (WithDirtyState).
// Doku: docs/dev-docs/core/modal-dirty-state.md

const DATE_PATTERN = /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2})(?::(\d{2}))?(?:\.\d+)?Z?)?$/;

// "2026-10-16", "2026-10-16 00:00", "2026-10-16T00:00:00.000000Z" ≙ "2026-10-16"
const normalizeDate = (value) => {
    const matches = value.match(DATE_PATTERN);

    if (!matches) {
        return value;
    }

    const time = matches[2] ? `${matches[2]}:${matches[3] ?? '00'}` : '00:00:00';

    return time === '00:00:00' ? matches[1] : `${matches[1]} ${time}`;
};

// Gleicht Darstellungsunterschiede aus, die keine echte Änderung sind
// (null ≙ '', 5 ≙ '5', Key-Reihenfolge) – Pendant zu normalizeDirtyValue() in PHP.
const normalize = (value) => {
    if (value === null || value === undefined) {
        return '';
    }

    // Ein leeres PHP-Array kommt als [] an; wire:model="prop.key" hängt String-Keys
    // daran, die map() verwerfen würde – solche Arrays wie Objekte behandeln.
    if (Array.isArray(value) && Object.keys(value).length === value.length) {
        return value.map(normalize);
    }

    if (typeof value === 'object') {
        return Object.keys(value).sort().reduce((carry, key) => {
            carry[key] = normalize(value[key]);

            return carry;
        }, {});
    }

    if (typeof value === 'boolean') {
        return value ? '1' : '0';
    }

    return normalizeDate(String(value));
};

// Tasten, die nur navigieren und keine Eingabe darstellen.
const NAVIGATION_KEYS = ['Tab', 'Shift', 'Control', 'Alt', 'Meta', 'Escape', 'CapsLock'];

const isUserInteraction = (event) => event.isTrusted
    && !(event.type === 'keydown' && NAVIGATION_KEYS.includes(event.key));

export const modalDirtyTracker = ({ props = [] } = {}) => ({
    trackedProps: props,
    baseline: null,
    forced: false,

    // Bis zur ersten echten Nutzerinteraktion folgt die Baseline dem State: Editoren,
    // Picker & Co. normalisieren ihren Wert beim Initialisieren (z. B. CKEditor
    // ergänzt <p>) – das ist keine Änderung des Nutzers.
    interacted: false,

    init() {
        this.snapshot();

        this.$watch('$wire.dirtyRevision', () => this.$nextTick(() => this.snapshot()));

        for (const type of ['pointerdown', 'keydown', 'paste', 'drop']) {
            this.$el.addEventListener(type, (event) => {
                if (isUserInteraction(event)) {
                    this.interacted = true;
                }
            }, { capture: true });
        }

        this.$watch('isDirty', (dirty) => {
            window.dispatchEvent(new CustomEvent('modal-dirty-changed', {
                detail: { modal: this.$el.closest('.modal')?.id ?? null, dirty },
            }));
        });
    },

    serialize() {
        const state = {};

        for (const prop of this.trackedProps) {
            state[prop] = normalize(this.$wire.$get(prop));
        }

        return JSON.stringify(state);
    },

    // Per x-effect am Root (von Alpine aufgeräumt): Baseline nachziehen, solange
    // der Nutzer noch nicht interagiert hat. Die Zuweisung erfolgt erst nach dem
    // laufenden Effect-Flush – Alpine reiht Bindings, die im selben Flush schon
    // liefen, sonst nicht erneut ein und der Footer bliebe auf "dirty" stehen.
    followPristineState() {
        this.serialize();

        if (this.interacted) {
            return;
        }

        queueMicrotask(() => {
            if (!this.interacted) {
                this.baseline = this.serialize();
            }
        });
    },

    snapshot() {
        this.forced = false;
        this.interacted = false;
        this.baseline = this.serialize();
    },

    markDirty() {
        this.forced = true;
    },

    get isDirty() {
        if (this.forced || this.$wire.dirtyForced) {
            return true;
        }

        return this.baseline !== null && this.serialize() !== this.baseline;
    },
});

export default (Alpine) => {
    Alpine.data('limaDirtyTracker', modalDirtyTracker);
};
